/**
 * Help tab content — hardcoded per the plan's confirmed scope decision
 * (#518 lands later by editing this structure + uploading resources; no
 * admin CMS in this build). Article text lives here; video/image media
 * blocks reference the platform resources registry by stable `code` and
 * render lazily via `useResource`/`ResourceVideo` in the `ArticleBlocks`
 * renderer (components/support/help/ArticleBlocks.tsx), used by the
 * full-page /help/:articleId.
 *
 * This module — not the renderer — is what the support widget shares: the
 * Help and Home tabs import these selectors, so changing the shape of
 * `helpArticles`/`helpCollections` reaches into the 384px panel.
 */

import { jobAidArticles } from './jobAidArticles';

/**
 * Inline markup, supported in every text-bearing field below (rendered by
 * ArticleBlocks' renderInline — never dangerouslySetInnerHTML):
 *   **bold**              emphasis
 *   [[UI Label]]          a UI chip matching literal on-screen text
 *   {{article-id|label}}  cross-link to another help article
 */

export interface HelpTextBlock {
  kind: 'text';
  text: string;
}

export interface HelpResourceBlock {
  kind: 'resource';
  code: string;
  caption?: string;
}

export interface HelpHeadingBlock {
  kind: 'heading';
  text: string;
}

/** Tinted, left-ruled aside. Default tone is informational teal. */
export interface HelpCalloutBlock {
  kind: 'callout';
  tone?: 'info' | 'tip' | 'warn';
  label: string;
  /** Body copy. A blank line ("\n\n") starts a new paragraph. */
  text?: string;
  items?: string[];
}

/** One numbered instruction; `result` renders as "You should now see: …". */
export interface HelpStepBlock {
  kind: 'step';
  num: string;
  action: string;
  result?: string;
}

export type HelpAnnotationTone = 'default' | 'ok' | 'warn';

/** Overlay drawn on a screenshot — % offsets relative to the image box. */
export interface HelpImageAnnotation {
  shape: 'box' | 'badge';
  tone?: HelpAnnotationTone;
  /** Badge text (step number / ✓). Ignored for boxes. */
  label?: string;
  left: string;
  top: string;
  width?: string;
  height?: string;
}

/** Static screenshot from public/assets/help (job-aid captures). */
export interface HelpImageBlock {
  kind: 'image';
  src: string;
  alt: string;
  caption?: string;
  annotations?: HelpImageAnnotation[];
}

export interface HelpChecklistBlock {
  kind: 'checklist';
  items: string[];
}

/**
 * Tabular content — most often a two-column troubleshooting table
 * ("What you see" / "What to do"), but the job aids also carry three- and
 * five-column reference tables, so the width is not fixed. Every row is
 * expected to have the same number of cells as `headers`.
 */
export interface HelpTableBlock {
  kind: 'table';
  headers: string[];
  rows: string[][];
}

export type HelpBlock =
  | HelpTextBlock
  | HelpResourceBlock
  | HelpHeadingBlock
  | HelpCalloutBlock
  | HelpStepBlock
  | HelpImageBlock
  | HelpChecklistBlock
  | HelpTableBlock;

export interface HelpArticle {
  id: string;
  collectionId: string;
  title: string;
  summary: string;
  blocks: HelpBlock[];
  /** Job-aid provenance (JA-00N), shown in the article meta row. */
  docId?: string;
  /** e.g. 'Org Admins' */
  audience?: string;
  /** e.g. '~4 minutes' */
  duration?: string;
  version?: string;
  /** ISO date (YYYY-MM-DD) of the last content revision. Drives "Recently updated". */
  updatedAt?: string;
}

/**
 * Icon key for a collection's Knowledge Center card. Maps to a lucide
 * component in the UI layer — this module stays React-free so it can be
 * imported by plain unit tests.
 */
export type HelpCollectionIcon =
  | 'getting-started'
  | 'clients'
  | 'clinical-workflows'
  | 'account'
  | 'insights'
  | 'organization-setup';

export interface HelpCollection {
  id: string;
  title: string;
  /** Emoji — still used by the compact in-widget Help tab. */
  icon: string;
  /** Lucide icon key for the full-page topic cards. */
  iconKey: HelpCollectionIcon;
  /** One-line card description (mockup's `.card p`). */
  description: string;
}

/**
 * Topic set, in the order the Knowledge Center grid shows them — the same six
 * the job aids are authored against, and the same six the approved mockup's
 * Documentation menu lists.
 *
 * Counts are always derived from `helpArticles`, never written down here. A
 * collection with no published article renders as a non-clickable "Coming
 * soon" card; every one of these currently has content.
 */
export const helpCollections: HelpCollection[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    icon: '🚀',
    iconKey: 'getting-started',
    description: 'Signing in, finding your way around, and where to get help.',
  },
  {
    id: 'clients',
    title: 'Clients & caseloads',
    icon: '👥',
    iconKey: 'clients',
    description: 'Client records, goals, assignments and caseload ownership.',
  },
  {
    id: 'clinical-workflows',
    title: 'Clinical workflows',
    icon: '🔴',
    iconKey: 'clinical-workflows',
    description: 'Raising, working and closing a Red Dot.',
  },
  {
    id: 'account',
    title: 'Account & permissions',
    icon: '🔐',
    iconKey: 'account',
    description: 'Access, roles, and what each person on your team can see.',
  },
  {
    id: 'insights',
    title: 'Insights & reporting',
    icon: '📈',
    iconKey: 'insights',
    description: 'Reading the Red Dot Report and interpreting trends across your caseload.',
  },
  {
    id: 'organization-setup',
    title: 'Organization setup',
    icon: '🏢',
    iconKey: 'organization-setup',
    description: 'Centers, locations and the structure your team works inside.',
  },
];

// Every article is a transcribed job aid — see jobAidArticles.ts. Listing
// them explicitly (rather than re-exporting the array) keeps the reading
// order here, next to the collections it mirrors.
const jobAid = (id: string): HelpArticle => {
  const article = jobAidArticles.find((a) => a.id === id);
  if (!article) throw new Error(`Missing job aid article: ${id}`);
  return article;
};

export const helpArticles: HelpArticle[] = [
  jobAid('sign-in-first-time'),
  jobAid('find-your-way-around'),
  jobAid('create-a-client-record'),
  jobAid('assign-client-to-clinician'),
  jobAid('set-the-overseeing-bcba'),
  jobAid('transfer-a-client'),
  jobAid('add-and-edit-goals'),
  jobAid('raise-a-red-dot'),
  jobAid('work-and-close-a-red-dot'),
  jobAid('add-a-team-member'),
  jobAid('remove-team-member-access'),
  jobAid('read-the-red-dot-report'),
  jobAid('create-and-manage-centers'),
];

/**
 * Curated running order for the "Popular articles" list. There is no
 * telemetry behind this yet — it is a deliberate editorial ordering, kept
 * here so it is obvious it needs revisiting when real signal exists.
 */
const POPULAR_ARTICLE_IDS = [
  'sign-in-first-time',
  'read-the-red-dot-report',
  'raise-a-red-dot',
  'assign-client-to-clinician',
  'find-your-way-around',
];

/** Every searchable string a block contributes (markup tokens left in — they
 *  are symbols, so substring matching on real words is unaffected). */
function blockSearchText(block: HelpBlock): string[] {
  switch (block.kind) {
    case 'text':
    case 'heading':
      return [block.text];
    case 'step':
      return [block.action, block.result ?? ''];
    case 'callout':
      return [block.label, block.text ?? '', ...(block.items ?? [])];
    case 'checklist':
      return block.items;
    case 'table':
      return block.rows.flat();
    default:
      return [];
  }
}

export function searchHelp(query: string): HelpArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return helpArticles.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.blocks.some((b) => blockSearchText(b).some((t) => t.toLowerCase().includes(q))),
  );
}

export function articlesForCollection(collectionId: string): HelpArticle[] {
  return helpArticles.filter((a) => a.collectionId === collectionId);
}

export function getCollection(collectionId: string): HelpCollection | undefined {
  return helpCollections.find((c) => c.id === collectionId);
}

export function getArticle(id: string): HelpArticle | undefined {
  return helpArticles.find((a) => a.id === id);
}

/** Number of numbered instructions in an article — the "N steps" meta. */
export function getStepCount(article: HelpArticle): number {
  return article.blocks.filter((b) => b.kind === 'step').length;
}

/**
 * Only the collections that actually have an article. Surfaces that cannot
 * express a "Coming soon" state — the compact widget Help tab, the article
 * page's sidebar — use this so they never show a dead end.
 */
export function collectionsWithArticles(): HelpCollection[] {
  return helpCollections.filter((c) => articlesForCollection(c.id).length > 0);
}

/** Collections paired with their real article count, in display order. */
export function collectionsWithCounts(): { collection: HelpCollection; count: number }[] {
  return helpCollections.map((collection) => ({
    collection,
    count: articlesForCollection(collection.id).length,
  }));
}

/** Newest first. Articles with no `updatedAt` sort last. */
export function recentlyUpdated(limit = 5): HelpArticle[] {
  return [...helpArticles]
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    .slice(0, limit);
}

/** The curated running order, skipping ids that no longer resolve. */
export function popularArticles(limit = 5): HelpArticle[] {
  return POPULAR_ARTICLE_IDS.map(getArticle)
    .filter((a): a is HelpArticle => a !== undefined)
    .slice(0, limit);
}

/** Previous/next article in `helpArticles` order, for the article footer. */
export function adjacentArticles(id: string): {
  previous?: HelpArticle;
  next?: HelpArticle;
} {
  const index = helpArticles.findIndex((a) => a.id === id);
  if (index === -1) return {};
  return {
    previous: helpArticles[index - 1],
    next: helpArticles[index + 1],
  };
}
